import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { API_PATHS } from "@/lib/api-paths";
import { API_BASE_URL } from "@/lib/config";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Database, 
  Send, 
  RefreshCw, 
  Settings, 
  Layers, 
  CheckCircle, 
  XCircle, 
  FileJson, 
  FileText, 
  Eye, 
  ExternalLink,
  Lock,
  Terminal,
  Grid,
  Maximize2,
  Minimize2
} from "lucide-react";

// ─── Specifications Data ──────────────────────────────────────────────────────

const API_SPECS = {
  api1: {
    title: "API No.1: Contract Webhook (Outbound to Client)",
    description: "SAP pushes contract master data to the client (EMP) platform when signed.",
    direction: "Outbound: SAP → Client (EMP)",
    endpoint: "POST [EMP_CONTRACT_WEBHOOK_URL]",
    fields: [
      { name: "salesNumber", type: "CHAR(10)", desc: "Contract Number (SAP VBELN)", sample: "C265007309" },
      { name: "shippingType", type: "CHAR(2)", desc: "Transportation Mode (VSART)", sample: "10" },
      { name: "shippingTypeDescription", type: "CHAR(20)", desc: "Shipping Mode description", sample: "Sea Freight" },
      { name: "soldTo", type: "CHAR(10)", desc: "Sold-To Party ID", sample: "0010001234" },
      { name: "soldToName", type: "CHAR(160)", desc: "Sold-To Party Name", sample: "Czarnikow Group Limited" },
      { name: "soldToPartyAddress", type: "CHAR(320)", desc: "Full Sold-To Address", sample: "4th floor, 2 More London Place" },
      { name: "country", type: "CHAR(3)", desc: "Country Key", sample: "GB" },
      { name: "countryName", type: "CHAR(30)", desc: "Country Name", sample: "United Kingdom" },
      { name: "shipFrom", type: "DATE(10)", desc: "Shipment Period from date", sample: "15.01.2026" },
      { name: "shipTo", type: "DATE(10)", desc: "Shipment Period to date", sample: "31.03.2026" },
      { name: "contractQty", type: "QUAN(13.3)", desc: "Contracted Quantity", sample: "625.000" },
      { name: "contractShip", type: "QUAN(15.3)", desc: "Already Shipped quantity sum", sample: "125.000" },
      { name: "contractBalance", type: "QUAN(15.3)", desc: "Contract Balance remaining (Qty - Shipped)", sample: "500.000" },
      { name: "incoTerm", type: "CHAR(3)", desc: "Incoterms (e.g., FOB, CIF)", sample: "FOB" },
      { name: "documentStatus", type: "CHAR(30)", desc: "Signed status descriptor", sample: "Signed" },
      { name: "division", type: "CHAR(2)", desc: "Division / Commodity", sample: "10" },
      { name: "divisionDescription", type: "CHAR(20)", desc: "Division description", sample: "Sugar Division" },
      { name: "containerType", type: "UNIT(3)", desc: "Container Type", sample: "40HC" },
      { name: "containerTypeDescription", type: "CHAR(30)", desc: "Container Size description", sample: "40ft High Cube" },
      { name: "portOfLoading", type: "CHAR(28)", desc: "Port of Loading Country", sample: "TH" },
      { name: "portOfLoadingName", type: "CHAR(28)", desc: "Port of Loading Port Name", sample: "THBKK" },
      { name: "portOfDischarge", type: "CHAR(28)", desc: "Port of Discharge Country", sample: "DE" },
      { name: "portOfDischargeName", type: "CHAR(28)", desc: "Port of Discharge Port Name", sample: "DEHAM" },
      { name: "docDate", type: "DATE(8)", desc: "Document Date (ERDAT)", sample: "31.10.2025" },
      { name: "productContracts", type: "Array", desc: "List of contract line items (Array)", sample: "[...]" }
    ],
    items: [
      { name: "itemNo", type: "NUMC(6)", desc: "Item No. in contract", sample: "000010" },
      { name: "sugarGroup", type: "CHAR(70)", desc: "Material Sugar Group desc", sample: "SUPER REFINED SUGAR" },
      { name: "sugarPacking", type: "CHAR(70)", desc: "Material Packing desc", sample: "50KG PP BAG" },
      { name: "material", type: "CHAR(40)", desc: "Material Code (VBAP-MATNR)", sample: "MAT001" },
      { name: "matDescription", type: "CHAR(40)", desc: "Material Description", sample: "Raw Sugar ICUMSA 45" },
      { name: "shippingMark", type: "CHAR(255)", desc: "Inspection text / Shipping Mark", sample: "SAMPLE BUYER CO LTD" },
      { name: "totalNetWeight", type: "QUAN(15.3)", desc: "Total Net Weight", sample: "25000" },
      { name: "totalNetWeightUnit", type: "CHAR(3)", desc: "Total Net Weight Unit", sample: "KG" },
      { name: "totalGrossWeight", type: "QUAN(15.3)", desc: "Total Gross Weight", sample: "26500" },
      { name: "totalGrossWeightUnit", type: "CHAR(3)", desc: "Total Gross Weight Unit", sample: "KG" },
      { name: "orderQty", type: "QUAN(15.3)", desc: "Total Package Quantity", sample: "1000" },
      { name: "salesUnit", type: "CHAR(3)", desc: "Total Package Unit", sample: "BAG" }
    ]
  },
  api2: {
    title: "API No.2: Create Sales Order (Inbound to SAP)",
    direction: "Inbound: Client (EMP) → SAP",
    endpoint: "POST /http/ffocean/createSO",
    fields: [
      { name: "empNo", type: "String(20)", desc: "EMP document reference ID", sample: "EMP0001234", req: false },
      { name: "salesOrg", type: "String(4)", desc: "Sales Organization ID", sample: "1000", req: true },
      { name: "distChnnl", type: "String(2)", desc: "Distribution Channel", sample: "10", req: true },
      { name: "division", type: "String(2)", desc: "Division / Product Line", sample: "00", req: true },
      { name: "docType", type: "String(4)", desc: "SAP Document Type (ZOR)", sample: "ZOR", req: false },
      { name: "refDocNo", type: "String(10)", desc: "Reference document / Contract No.", sample: "CON-SAMPLE", req: true },
      { name: "soldToParty", type: "String(10)", desc: "Sold-To Business Partner ID", sample: "0010001234", req: true },
      { name: "shipToParty", type: "String(10)", desc: "Ship-To Business Partner ID", sample: "0010001234", req: true },
      { name: "shippingType", type: "String(2)", desc: "Shipping Type code", sample: "10", req: true },
      { name: "docCurrency", type: "String(3)", desc: "Document Currency code", sample: "USD", req: true },
      { name: "bookingNo", type: "String(20)", desc: "Booking Reference No.", sample: "BKNG123456", req: false },
      { name: "vesselName", type: "String(50)", desc: "Vessel and voyage info", sample: "EVER GIVEN 123W", req: false },
      { name: "etdDate", type: "String(10)", desc: "ETD Date (dd.mm.yyyy format)", sample: "15.01.2025", req: false },
      { name: "soItem", type: "Array", desc: "List of items to create (Array)", sample: "[...]", req: true }
    ],
    items: [
      { name: "itemNo", type: "String(6)", desc: "Item sequence number", sample: "000010", req: true },
      { name: "material", type: "String(40)", desc: "Material No.", sample: "MAT001", req: true },
      { name: "orderQty", type: "String(17)", desc: "Order Quantity", sample: "100", req: true },
      { name: "salesUnit", type: "String(3)", desc: "Sales Unit (e.g. TON)", sample: "TON", req: true },
      { name: "refDocNo", type: "String(10)", desc: "Reference Contract No.", sample: "CON-SAMPLE", req: true },
      { name: "refDocItem", type: "String(4)", desc: "Reference Contract Item ID", sample: "0010", req: true }
    ]
  },
  api3: {
    title: "API No.3: Sales Order Update Webhook (Outbound to Client)",
    direction: "Outbound: SAP → Client (EMP)",
    endpoint: "POST [EMP_SALES_ORDER_WEBHOOK_URL]",
    fields: [
      { name: "salesDoc", type: "CHAR(10)", desc: "Sales Document / Invoice Number", sample: "1000000001" },
      { name: "bookingNumber", type: "CHAR(20)", desc: "Shipping Booking Number", sample: "BKNG123456" },
      { name: "vesselName", type: "CHAR(50)", desc: "Vessel name and voyage info", sample: "EVER GIVEN 123W" },
      { name: "etd", type: "DATE(8)", desc: "Estimated Departure (dd.mm.yyyy)", sample: "15.01.2025" },
      { name: "eta", type: "DATE(8)", desc: "Estimated Arrival (dd.mm.yyyy)", sample: "15.02.2025" },
      { name: "deliveryDate", type: "DATE(8)", desc: "Cargo Ready / Delivery Date", sample: "15.02.2025" },
      { name: "portOfLoading", type: "CHAR(40)", desc: "Port of Loading (POL) Code", sample: "THBKK" },
      { name: "portOfDischarge", type: "CHAR(40)", desc: "Port of Discharge (POD) Code", sample: "DEHAM" },
      { name: "finalDestination", type: "CHAR(40)", desc: "Final Destination (POD)", sample: "NLRTM" },
      { name: "empNo", type: "CHAR(20)", desc: "EMP document reference ID", sample: "EMP0001234" },
      { name: "docStatus", type: "CHAR(30)", desc: "Sales Order Status", sample: "In Progress" },
      { name: "soItem", type: "Array", desc: "List of items with logistics status (Array)", sample: "[...]" }
    ],
    items: [
      { name: "itemNo", type: "NUMC(6)", desc: "Item No.", sample: "000010" },
      { name: "containerType", type: "UNIT(3)", desc: "Container Type", sample: "40HC" },
      { name: "noOfContainer", type: "DEC(5.2)", desc: "No of Container", sample: "2" },
      { name: "orderQty", type: "QUAN(15.3)", desc: "Order Qty (TON)", sample: "100" },
      { name: "salesUnit", type: "CHAR(3)", desc: "Sales Unit (e.g. TON)", sample: "TON" },
      { name: "descriptionOfGoods", type: "STRING", desc: "Description of Goods", sample: "RAW CANE SUGAR ICUMSA 45" },
      { name: "conNo", type: "CHAR(15)", desc: "Container Number", sample: "TCKU1234567" },
      { name: "sealNo", type: "CHAR(20)", desc: "Seal Number", sample: "SL123456" }
    ]
  },
  api4: {
    title: "API No.4: Update Sales Order (Inbound to SAP)",
    direction: "Inbound: Client (EMP) → SAP",
    endpoint: "POST /http/ffocean/updateSO",
    fields: [
      { name: "empNo", type: "String(20)", desc: "EMP document reference ID", sample: "EMP0001234", req: false },
      { name: "salesOrderNo", type: "String(12)", desc: "Existing SAP Sales Order Number", sample: "1000000001", req: true },
      { name: "bookingNo", type: "String(20)", desc: "Booking Reference No.", sample: "BKNG123456", req: false },
      { name: "vesselName", type: "String(50)", desc: "Vessel Name", sample: "EVER GIVEN 123W", req: false },
      { name: "etdDate", type: "String(10)", desc: "ETD Date (dd.mm.yyyy)", sample: "15.01.2025", req: false },
      { name: "fwdAgent", type: "String(10)", desc: "Forwarder Business Partner ID", sample: "0010005678", req: false },
      { name: "fwdContact", type: "String(100)", desc: "Forwarding Agent Contact", sample: "MR. FORWARDING AGENT", req: false },
      { name: "soItem", type: "Array", desc: "List of items to update (Array)", sample: "[...]", req: true }
    ],
    items: [
      { name: "itemNo", type: "String(6)", desc: "Item No.", sample: "000010", req: true },
      { name: "material", type: "String(40)", desc: "Material No.", sample: "MAT001", req: true },
      { name: "orderQty", type: "String(17)", desc: "Order Qty", sample: "100", req: true },
      { name: "noOfContainer", type: "String(17)", desc: "No. of containers", sample: "2", req: false },
      { name: "soDocCont", type: "Array", desc: "List of containers details (Array)", sample: "[...]", req: false }
    ]
  }
};

// ─── Helper Types ───────────────────────────────────────────────────────────

interface ApiResponse {
  status: number;
  data: unknown;
  ok: boolean;
}

interface SapConfig {
  sapWebhookUrl: string;
  sapWebhookApiKey: string;
  simulateError: {
    SAP_CREATE_SO: boolean;
    SAP_UPDATE_SO: boolean;
  };
}

interface SapSalesOrder {
  id: number;
  salesOrderNo: string;
  empNo: string | null;
  action: string;
  returnType: string;
  returnMessage: string | null;
  createdAt: string;
}

interface SapOutboundWebhook {
  id: number;
  webhookType: string;
  targetUrl: string | null;
  statusCode: number | null;
  success: number;
  sentAt: string;
  payload: any;
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

function ResponseBlock({ resp }: { resp: ApiResponse | null }) {
  if (!resp) return null;
  const ok = resp.ok;
  return (
    <div className={`mt-3 rounded-lg border p-4 text-sm font-mono transition-all ${ok ? "border-emerald-300 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/40" : "border-rose-300 bg-rose-50/70 dark:border-rose-900/60 dark:bg-rose-950/40"}`}>
      <div className="mb-2 flex items-center justify-between border-b border-border/50 pb-1.5">
        <span className={`text-xs font-semibold uppercase tracking-wider ${ok ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}>
          HTTP Response Status: {resp.status}
        </span>
        <span className="text-[10px] text-muted-foreground opacity-60">JSON payload</span>
      </div>
      <pre className="whitespace-pre-wrap break-all text-xs leading-relaxed max-h-[350px] overflow-auto">{JSON.stringify(resp.data, null, 2)}</pre>
    </div>
  );
}

function SectionCard({
  title,
  icon: Icon,
  children,
  onFullscreenToggle,
  isFullscreen
}: {
  title: string;
  icon?: React.ComponentType<any>;
  children: React.ReactNode;
  onFullscreenToggle?: () => void;
  isFullscreen?: boolean;
}) {
  return (
    <Card className="border-white/10 dark:bg-black/40 backdrop-blur-md shadow-lg overflow-hidden transition-all duration-300 hover:shadow-primary/5">
      <CardHeader className="bg-muted/40 pb-4 shrink-0 flex flex-row items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="size-5 text-primary" />}
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
        </div>
        {onFullscreenToggle && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onFullscreenToggle}
            className="size-8 text-muted-foreground hover:text-foreground"
          >
            {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-5 space-y-4">
        {children}
      </CardContent>
    </Card>
  );
}

function Badge({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
        ok 
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-500/20" 
          : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-500/20"
      }`}
    >
      <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-rose-500"}`} />
      {label}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function SapSimulationPage({ token }: { token: string | null }) {
  const [activeTab, setActiveTab] = useState<"specs" | "api1" | "api2" | "api3" | "api4" | "history" | "config">("specs");

  // Config state
  const [config, setConfig] = useState<SapConfig>({
    sapWebhookUrl: "",
    sapWebhookApiKey: "",
    simulateError: { SAP_CREATE_SO: false, SAP_UPDATE_SO: false },
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configResp, setConfigResp] = useState<ApiResponse | null>(null);

  // SAP Basic Auth
  const [sapUsername, setSapUsername] = useState("");
  const [sapPassword, setSapPassword] = useState("");

  // Spec viewing helper
  const [selectedSpec, setSelectedSpec] = useState<keyof typeof API_SPECS>("api1");

  // Webhook and CPI Forms state
  const [contractPayload, setContractPayload] = useState("{}");
  const [contractResp, setContractResp] = useState<ApiResponse | null>(null);
  const [contractLoading, setContractLoading] = useState(false);

  const [createSoBody, setCreateSoBody] = useState("");
  const [createSoResp, setCreateSoResp] = useState<ApiResponse | null>(null);
  const [createSoLoading, setCreateSoLoading] = useState(false);

  const [soPayload, setSoPayload] = useState("{}");
  const [soResp, setSoResp] = useState<ApiResponse | null>(null);
  const [soLoading, setSoLoading] = useState(false);

  const [updateSoBody, setUpdateSoBody] = useState("");
  const [updateSoResp, setUpdateSoResp] = useState<ApiResponse | null>(null);
  const [updateSoLoading, setUpdateSoLoading] = useState(false);

  // History tables
  const [salesOrders, setSalesOrders] = useState<SapSalesOrder[]>([]);
  const [salesOrdersLoading, setSalesOrdersLoading] = useState(false);
  const [outboundWebhooks, setOutboundWebhooks] = useState<SapOutboundWebhook[]>([]);
  const [outboundLoading, setOutboundLoading] = useState(false);

  // Search states
  const [searchCreateSO, setSearchCreateSO] = useState("");
  const [searchUpdateSO, setSearchUpdateSO] = useState("");
  const [searchHistory, setSearchHistory] = useState("");

  // Process attempt counts
  const [processAttemptCounts, setProcessAttemptCounts] = useState<Record<number, number>>({});

  // Interactive Form Builders
  const [creatorSubTab, setCreatorSubTab] = useState<"header" | "logistics" | "partners" | "items">("header");
  const [selectedItemIdx, setSelectedItemIdx] = useState<number>(0);
  const [expandedSoId, setExpandedSoId] = useState<number | null>(null);

  const updateContractField = (path: string, value: any) => {
    try {
      const parsed = JSON.parse(contractPayload);
      const keys = path.split(".");
      let current = parsed;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (k.match(/^\d+$/)) {
          const idx = parseInt(k, 10);
          current = current[idx];
        } else {
          if (!current[k]) current[k] = {};
          current = current[k];
        }
      }
      const lastKey = keys[keys.length - 1];
      if (lastKey.match(/^\d+$/)) {
        const idx = parseInt(lastKey, 10);
        current[idx] = value;
      } else {
        current[lastKey] = value;
      }

      // Sync salesNumber & saleNumber
      if (lastKey === "salesNumber") {
        parsed.saleNumber = value;
      } else if (lastKey === "saleNumber") {
        parsed.salesNumber = value;
      }

      // If we updated item level qty weights, automatically do some helpful basic math
      if (path.includes("productContracts") || path.includes("soItem")) {
        const itemIdx = parseInt(keys[1], 10);
        const item = parsed.productContracts?.[itemIdx] || parsed.soItem?.[itemIdx];
        if (item) {
          const qtyVal = parseFloat(item.contractQty) || 0;
          const shipVal = parseFloat(item.contractShip) || 0;
          item.contractBalance = (qtyVal - shipVal).toFixed(3) + " ";
        }
      }

      setContractPayload(JSON.stringify(parsed, null, 2));
    } catch (e) {
      console.error("Failed to update contract field:", e);
    }
  };

  const [soCreatorSubTab, setSoCreatorSubTab] = useState<"header" | "logistics" | "partners" | "items">("header");
  const [soSelectedIdx, setSoSelectedIdx] = useState<number>(0);
  const [activeContIdx, setActiveContIdx] = useState<number>(0);
  const [fullscreenCard, setFullscreenCard] = useState<string | null>(null);

  const updateSoField = (path: string, value: any) => {
    try {
      const parsed = JSON.parse(soPayload);
      const keys = path.split(".");
      let current = parsed;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (k.match(/^\d+$/)) {
          const idx = parseInt(k, 10);
          current = current[idx];
        } else {
          if (!current[k]) current[k] = {};
          current = current[k];
        }
      }
      const lastKey = keys[keys.length - 1];
      if (lastKey.match(/^\d+$/)) {
        const idx = parseInt(lastKey, 10);
        current[idx] = value;
      } else {
        current[lastKey] = value;
      }

      setSoPayload(JSON.stringify(parsed, null, 2));
    } catch (e) {
      console.error("Failed to update SO field:", e);
    }
  };

  const linkToSoWebhook = async (soRecord: SapSalesOrder) => {
    setProcessAttemptCounts(prev => ({
      ...prev,
      [soRecord.id]: (prev[soRecord.id] || 0) + 1
    }));
    try {
      const res = await api.get<{ payload: any }>(API_PATHS.SAP_DEFAULT_SO_PAYLOAD, { token });
      const defaultSo = res.ok ? (res.data as any)?.payload : {};
      
      const bodyObj = (soRecord.requestPayload as any) || {};
      const soHeader = bodyObj.data?.soHeader || {};
      
      const pick = (input: any, fallback: any) =>
        input !== undefined && input !== null && input !== "" ? input : fallback;

      // Interface #3 dates are DATE(8) / yyyymmdd. Incoming createSO dates arrive
      // as dd.mm.yyyy — convert so the webhook payload matches the spec.
      const toYyyymmdd = (value: any): any => {
        if (typeof value !== "string") return value;
        const m = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        return m ? `${m[3]}${m[2]}${m[1]}` : value;
      };

      // Extract items list from incoming create/update request, or fallback to default item
      const incomingItems: any[] = Array.isArray(soHeader.soItem) && soHeader.soItem.length > 0 
        ? soHeader.soItem 
        : (defaultSo.soItem || []);

      const mappedItems = incomingItems.map((item: any, idx: number) => {
        const itemNo = pick(item.itemNo, String((idx + 1) * 10).padStart(6, "0"));
        const material = pick(item.material, "S400120002");
        const descriptionOfGoods = pick(item.descOfGoods || item.matDescription || item.descriptionOfGoods, "CANE SUGAR SPECIFICATIONS");
        const containertype = pick(item.containertype || item.containerType, "20GP");
        const noOfContainer = pick(item.noOfContainer || item.numberOfContainer, "1");
        const salesUnitTon = pick(item.salesUnit || item.salesUnitTon, "TON");
        const totalGrossWeight = pick(item.totalGrossWeight || item.grossWeight, "28900");

        // Map containers (docCont or soDocCont)
        const incomingConts = item.docCont || item.soDocCont || [];
        let mappedConts: any[] = [];
        if (Array.isArray(incomingConts) && incomingConts.length > 0) {
          mappedConts = incomingConts.map((c: any) => ({
            conNo: pick(c.conNo || c.containerNo, "TCKU" + Math.floor(1000000 + Math.random() * 9000000)),
            sealNo: pick(c.sealNo, "SL" + Math.floor(100000 + Math.random() * 900000)),
            tareWeight: pick(c.tareWeight, "3900"),
            contPo: pick(c.contPo, itemNo),
            descOfGoods: pick(c.descOfGoods || c.descriptionOfGoods, descriptionOfGoods),
            quantity: pick(c.quantity, "25"),
            unit: pick(c.unit, salesUnitTon),
            netWeight: pick(c.netWeight, "25000"),
            grossWeight: pick(c.grossWeight, "28900"),
            cbm: pick(c.cbm, "33.5")
          }));
        } else {
          // Default mock container for the item
          mappedConts = [
            {
              conNo: "TCKU" + Math.floor(1000000 + Math.random() * 9000000),
              sealNo: "SL" + Math.floor(100000 + Math.random() * 900000),
              tareWeight: "3900",
              contPo: itemNo,
              descOfGoods: descriptionOfGoods,
              quantity: "25",
              unit: salesUnitTon,
              netWeight: "25000",
              grossWeight: totalGrossWeight,
              cbm: "33.5"
            }
          ];
        }

        // Order quantity should match what's actually loaded into the containers,
        // not a stray "0" carried over from an incomplete createSO submission.
        const containerQtySum = mappedConts.reduce(
          (sum, c) => sum + (parseFloat(c.quantity) || 0),
          0
        );
        const orderQtyTonRaw = pick(item.orderQty || item.orderQtyTon, "");
        const orderQtyTon =
          orderQtyTonRaw !== "" && parseFloat(orderQtyTonRaw) > 0
            ? orderQtyTonRaw
            : String(containerQtySum || 25);

        return {
          itemNo,
          material,
          descriptionOfGoods,
          containertype,
          noOfContainer,
          orderQtyTon,
          salesUnitTon,
          totalGrossWeight,
          docCont: mappedConts
        };
      });

      const finalDestination = pick(soHeader.finalDestPort, defaultSo.finalDestination);
      const finalDestinationCountry = pick(soHeader.finalDestCountry, defaultSo.finalDestinationCountry);
      // destinationPoint / portOfDestination describe the same place as
      // finalDestination — keep them aligned instead of leaking the base default.
      const destinationPoint = finalDestinationCountry
        ? `${finalDestination},${finalDestinationCountry}`
        : finalDestination;

      const loadingDateRaw = pick(soHeader.loadingDate, "");
      // Interface #3 defines laycansatThailandPort as CHAR(20) — a "from-to" date
      // range, not a single date, even though only one loading date is captured here.
      const laycansatThailandPort = loadingDateRaw
        ? `${loadingDateRaw}-${loadingDateRaw}`
        : defaultSo.laycansatThailandPort;

      const payload = {
        ...defaultSo,
        salesDoc: soRecord.salesOrderNo || pick(soHeader.salesOrderNo, "0265007416"),
        empNo: pick(soHeader.empNo, pick(soRecord.empNo, defaultSo.empNo)),
        portOfLoading: pick(soHeader.loadingPort, defaultSo.portOfLoading),
        portOfDischarge: pick(soHeader.dischargePort, defaultSo.portOfDischarge),
        destinationPoint,
        portOfDestination: destinationPoint,
        finalDestination,
        finalDestinationCountry,
        etd: toYyyymmdd(pick(soHeader.etdDate, defaultSo.etd)),
        eta: toYyyymmdd(pick(soHeader.etdDatePod, defaultSo.eta)),
        deliveryDate: toYyyymmdd(pick(soHeader.reqDeliveryDate, defaultSo.deliveryDate)),
        bookingNumber: pick(soHeader.bookingNo, defaultSo.bookingNumber),
        customerPo: pick(soHeader.customerPoNo, defaultSo.customerPo),
        laycansatThailandPort,
        vesselName: pick(soHeader.vesselName, defaultSo.vesselName),
        loadingDate: toYyyymmdd(loadingDateRaw || defaultSo.loadingDate),
        closingDate: toYyyymmdd(pick(soHeader.contClosingDate, defaultSo.closingDate)),
        forwardingAgent: pick(soHeader.fwdAgent, defaultSo.forwardingAgent),
        soldTo: pick(soHeader.soldTo, defaultSo.soldTo),
        soldToName: pick(soHeader.soldToName, defaultSo.soldToName),
        soldToAddress: pick(soHeader.soldToPartyAddress, defaultSo.soldToAddress),
        country: pick(soHeader.country, defaultSo.country),
        countryName: pick(soHeader.countryName, defaultSo.countryName),
        soItem: mappedItems
      };

      setSoPayload(JSON.stringify(payload, null, 2));
      setActiveTab("api3");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      console.error(e);
    }
  };

  // ── Loading Functions ────────────────────────────────────────────────────────

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    const res = await api.get<{ success: boolean; config: SapConfig }>(API_PATHS.SAP_CONFIG, { token });
    setConfigLoading(false);
    if (res.ok && (res.data as any)?.config) {
      setConfig((res.data as any).config as SapConfig);
    }
  }, [token]);

  const loadSalesOrders = useCallback(async () => {
    setSalesOrdersLoading(true);
    const res = await api.get<{ data: SapSalesOrder[] }>(API_PATHS.SAP_SALES_ORDERS, { token });
    setSalesOrdersLoading(false);
    if (res.ok) setSalesOrders(((res.data as any)?.data as SapSalesOrder[]) ?? []);
  }, [token]);

  const loadOutboundWebhooks = useCallback(async () => {
    setOutboundLoading(true);
    const res = await api.get<{ data: SapOutboundWebhook[] }>(API_PATHS.SAP_OUTBOUND_WEBHOOKS, { token });
    setOutboundLoading(false);
    if (res.ok) setOutboundWebhooks(((res.data as any)?.data as SapOutboundWebhook[]) ?? []);
  }, [token]);

  const loadDefaultContractPayload = async () => {
    const res = await api.get<{ payload: unknown }>(API_PATHS.SAP_DEFAULT_CONTRACT_PAYLOAD, { token });
    if (res.ok) setContractPayload(JSON.stringify((res.data as any)?.payload, null, 2));
  };

  const loadDefaultSoPayload = async () => {
    const res = await api.get<{ payload: unknown }>(API_PATHS.SAP_DEFAULT_SO_PAYLOAD, { token });
    if (res.ok) setSoPayload(JSON.stringify((res.data as any)?.payload, null, 2));
  };

  useEffect(() => {
    loadConfig();
    loadSalesOrders();
    loadOutboundWebhooks();
    loadDefaultContractPayload();
    loadDefaultSoPayload();
    // Pre-populate createSO and updateSO textareas
    setCreateSoBody(JSON.stringify(INITIAL_CREATE_SO, null, 2));
    setUpdateSoBody(JSON.stringify(INITIAL_UPDATE_SO, null, 2));
  }, [loadConfig, loadSalesOrders, loadOutboundWebhooks]);

  // ── Save Configuration ─────────────────────────────────────────────────────

  const saveConfig = async () => {
    setConfigSaving(true);
    const res = await api.put(API_PATHS.SAP_CONFIG, {
      token,
      body: {
        sapWebhookUrl: config.sapWebhookUrl,
        sapWebhookApiKey: config.sapWebhookApiKey,
        sapCreateSo: config.simulateError.SAP_CREATE_SO,
        sapUpdateSo: config.simulateError.SAP_UPDATE_SO,
      },
    });
    setConfigSaving(false);
    setConfigResp(res);
    if (res.ok && (res.data as any)?.config) setConfig((res.data as any).config as SapConfig);
  };

  // ── Actions ────────────────────────────────────────────────────────────────

  const buildBasicAuth = () => {
    if (!sapUsername && !sapPassword) return undefined;
    return `Basic ${btoa(`${sapUsername}:${sapPassword}`)}`;
  };

  const callCreateSo = async () => {
    let body: unknown;
    try {
      body = JSON.parse(createSoBody);
    } catch {
      setCreateSoResp({ status: 0, data: "Invalid JSON in request body", ok: false });
      return;
    }
    setCreateSoLoading(true);
    const auth = buildBasicAuth();
    const res = await api.post(API_PATHS.SAP_CREATE_SO, {
      token,
      body,
      headers: auth ? { Authorization: auth } : {},
    });
    setCreateSoLoading(false);
    setCreateSoResp(res);
    loadSalesOrders();
  };

  const callUpdateSo = async () => {
    let body: unknown;
    try {
      body = JSON.parse(updateSoBody);
    } catch {
      setUpdateSoResp({ status: 0, data: "Invalid JSON in request body", ok: false });
      return;
    }
    setUpdateSoLoading(true);
    const auth = buildBasicAuth();
    const res = await api.post(API_PATHS.SAP_UPDATE_SO, {
      token,
      body,
      headers: auth ? { Authorization: auth } : {},
    });
    setUpdateSoLoading(false);
    setUpdateSoResp(res);
    loadSalesOrders();
  };

  const sendContractWebhook = async () => {
    let body: unknown = {};
    try {
      body = JSON.parse(contractPayload);
    } catch {
      setContractResp({ status: 0, data: "Invalid JSON in payload", ok: false });
      return;
    }
    setContractLoading(true);
    const res = await api.post(API_PATHS.SAP_WEBHOOK_SEND_CONTRACT, { token, body });
    setContractLoading(false);
    setContractResp(res);
    loadOutboundWebhooks();
  };

  const sendSoWebhook = async () => {
    let body: unknown = {};
    try {
      body = JSON.parse(soPayload);
    } catch {
      setSoResp({ status: 0, data: "Invalid JSON in payload", ok: false });
      return;
    }
    setSoLoading(true);
    const res = await api.post(API_PATHS.SAP_WEBHOOK_SEND_SALES_ORDER, { token, body });
    setSoLoading(false);
    setSoResp(res);
    loadOutboundWebhooks();
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  // ── Render Tabs ────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-stretch w-full min-h-0 flex-1">
      
      {/* Sidebar Tabs */}
      <div className="flex flex-col gap-1 w-full shrink-0 md:w-64 border-r border-border/60 py-1 pr-4">
        <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 px-3">
          SAP CPI Interfaces
        </h2>
        {[
          { id: "specs", label: "API Specifications", icon: Grid },
          { id: "api1", label: "API No.1: Send Contract", icon: Send },
          { id: "api2", label: "API No.2: Create SO Mock", icon: Database },
          { id: "api3", label: "API No.3: Send SO Webhook", icon: Send },
          { id: "api4", label: "API No.4: Update SO Mock", icon: Database },
          { id: "history", label: "SAP Mock History Logs", icon: FileText },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id as any)}
              className={`flex items-center gap-3 text-sm font-medium px-3.5 py-2.5 rounded-lg transition-all text-left w-full ${
                activeTab === t.id
                  ? "bg-primary/10 text-primary border-l-4 border-primary"
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <Icon className="size-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        
        {/* Specifications Tab */}
        {activeTab === "specs" && (
          <div className="space-y-6 animate-in fade-in duration-200">
            <div>
              <h2 className="text-xl font-bold text-foreground">SAP API Specifications</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Browse official schemas and fields derived from the <code>sap-spec/</code> CSV documentation.
              </p>
            </div>

            <div className="flex gap-2 flex-wrap border-b border-border pb-3">
              {Object.entries(API_SPECS).map(([key, value]) => (
                <Button
                  key={key}
                  variant={selectedSpec === key ? "default" : "outline"}
                  onClick={() => setSelectedSpec(key as any)}
                  className="text-xs"
                >
                  {key.toUpperCase()}: {value.title.split(":")[0]}
                </Button>
              ))}
            </div>

            <Card className="border-white/10 shadow-md">
              <CardHeader className="bg-muted/30 pb-3 border-b border-white/5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base font-semibold">{API_SPECS[selectedSpec].title}</CardTitle>
                    <CardDescription className="text-xs mt-1 text-muted-foreground">{API_SPECS[selectedSpec].description}</CardDescription>
                  </div>
                  <span className="text-[10px] font-bold bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded uppercase font-mono">
                    {API_SPECS[selectedSpec].direction}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="flex items-center gap-2 p-3 bg-black/30 dark:bg-black/60 rounded-md border border-white/5 text-xs font-mono text-primary">
                  <Terminal className="size-4 shrink-0" />
                  <span>{API_SPECS[selectedSpec].endpoint}</span>
                </div>

                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] tracking-wider border-b border-border">
                      <tr>
                        <th className="p-3">Field Name</th>
                        <th className="p-3">Data Type</th>
                        <th className="p-3">Description</th>
                        <th className="p-3">Sample Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {API_SPECS[selectedSpec].fields.map((field) => (
                        <tr key={field.name} className="hover:bg-muted/25 transition-colors">
                          <td className="p-3 font-mono font-semibold text-foreground">
                            {field.name}
                            {(field as any).req && <span className="text-destructive ml-1">*</span>}
                          </td>
                          <td className="p-3 font-mono text-muted-foreground">{field.type}</td>
                          <td className="p-3">{field.desc}</td>
                          <td className="p-3 font-mono text-blue-600 dark:text-blue-400">{field.sample}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {API_SPECS[selectedSpec].items && (
                  <div className="space-y-3 pt-2">
                    <h4 className="text-sm font-semibold text-foreground">Line Item Specification (soItem / productContracts)</h4>
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] tracking-wider border-b border-border">
                          <tr>
                            <th className="p-3">Item Field Name</th>
                            <th className="p-3">Data Type</th>
                            <th className="p-3">Description</th>
                            <th className="p-3">Sample Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {API_SPECS[selectedSpec].items.map((field) => (
                            <tr key={field.name} className="hover:bg-muted/25 transition-colors">
                              <td className="p-3 font-mono font-semibold text-foreground">
                                {field.name}
                                {(field as any).req && <span className="text-destructive ml-1">*</span>}
                              </td>
                              <td className="p-3 font-mono text-muted-foreground">{field.type}</td>
                              <td className="p-3">{field.desc}</td>
                              <td className="p-3 font-mono text-blue-600 dark:text-blue-400">{field.sample}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* API No.1: Contract Webhook Tab */}
        {activeTab === "api1" && (
          <div className="space-y-6 animate-in fade-in duration-200">
            <div>
              <h2 className="text-xl font-bold text-foreground">API No.1: Produce Contract JSON & Webhook</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Generate valid contract payloads conforming to <code>[EMP x SAP] API Overview for Review - API No.1.csv</code>.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-12 items-start">
              {fullscreenCard !== "api1_json" && (
                <div className={`${fullscreenCard === "api1_form" ? "lg:col-span-12" : "lg:col-span-7"} space-y-6 transition-all duration-300`}>
                  <SectionCard
                    title="Interactive Payload Creator"
                    icon={Grid}
                    onFullscreenToggle={() => setFullscreenCard(fullscreenCard === "api1_form" ? null : "api1_form")}
                    isFullscreen={fullscreenCard === "api1_form"}
                  >
                {(() => {
                  let parsed: any = null;
                  try {
                    parsed = JSON.parse(contractPayload);
                  } catch {}

                  if (!parsed || typeof parsed !== "object") {
                    return (
                      <div className="py-8 text-center border border-dashed border-rose-300 dark:border-rose-900/40 rounded-lg bg-rose-50/50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400">
                        <XCircle className="size-8 mx-auto mb-2 text-rose-500" />
                        <p className="text-sm font-semibold">Invalid JSON in Editor</p>
                        <p className="text-xs text-muted-foreground mt-1">Please fix the syntax in the JSON Editor on the right to use the builder.</p>
                      </div>
                    );
                  }

                  const itemsList = parsed.productContracts || parsed.soItem || [];

                  return (
                    <div className="space-y-4">
                      {/* Creator Sub-Tabs */}
                      <div className="flex border-b border-border/60 pb-1.5 gap-1 overflow-x-auto">
                        {[
                          { id: "header", label: "Header" },
                          { id: "logistics", label: "Logistics" },
                          { id: "partners", label: "Partners" },
                          { id: "items", label: `Items (${itemsList.length})` },
                        ].map((subTab) => (
                          <button
                            key={subTab.id}
                            type="button"
                            onClick={() => setCreatorSubTab(subTab.id as any)}
                            className={`text-xs px-2.5 py-1.5 rounded transition-all font-medium whitespace-nowrap ${
                              creatorSubTab === subTab.id
                                ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            }`}
                          >
                            {subTab.label}
                          </button>
                        ))}
                      </div>

                      {/* Tab 1: Header */}
                      {creatorSubTab === "header" && (
                        <div className="space-y-3 animate-in fade-in duration-150">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formContractNo">Contract Number (salesNumber)</Label>
                              <Input
                                id="formContractNo"
                                value={parsed.salesNumber || ""}
                                onChange={(e) => updateContractField("salesNumber", e.target.value)}
                                placeholder="e.g. C0265007416"
                              />
                            </div>
                            <div>
                              <Label htmlFor="formDocDate">Doc Date (docDate)</Label>
                              <Input
                                id="formDocDate"
                                value={parsed.docDate || ""}
                                onChange={(e) => updateContractField("docDate", e.target.value)}
                                placeholder="YYYYMMDD"
                              />
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formDocStatus">Doc Status (documentStatus)</Label>
                              <Select
                                value={parsed.documentStatus || "Draft"}
                                onValueChange={(val) => updateContractField("documentStatus", val)}
                              >
                                <SelectTrigger id="formDocStatus">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {["Draft", "Signed", "Active", "Released"].map((st) => (
                                    <SelectItem key={st} value={st}>{st}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="formDivision">Division</Label>
                              <Input
                                id="formDivision"
                                value={parsed.division || ""}
                                onChange={(e) => updateContractField("division", e.target.value)}
                                placeholder="e.g. 01"
                              />
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formDivDesc">Division Description</Label>
                              <Input
                                id="formDivDesc"
                                value={parsed.divisionDescription || ""}
                                onChange={(e) => updateContractField("divisionDescription", e.target.value)}
                                placeholder="e.g. Sugar"
                              />
                            </div>
                            <div>
                              <Label htmlFor="formContainerType">Container Type</Label>
                              <Input
                                id="formContainerType"
                                value={parsed.containerType || ""}
                                onChange={(e) => updateContractField("containerType", e.target.value)}
                                placeholder="e.g. ZC1"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="formContainerTypeDesc">Container Type Description</Label>
                            <Input
                              id="formContainerTypeDesc"
                              value={parsed.containerTypeDescription || ""}
                              onChange={(e) => updateContractField("containerTypeDescription", e.target.value)}
                              placeholder="e.g. 20’ GP"
                            />
                          </div>
                        </div>
                      )}

                      {/* Tab 2: Logistics */}
                      {creatorSubTab === "logistics" && (
                        <div className="space-y-3 animate-in fade-in duration-150">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formShippingType">Shipping Type</Label>
                              <Input
                                id="formShippingType"
                                value={parsed.shippingType || ""}
                                onChange={(e) => updateContractField("shippingType", e.target.value)}
                                placeholder="e.g. M1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="formShippingTypeDesc">Shipping Description</Label>
                              <Input
                                id="formShippingTypeDesc"
                                value={parsed.shippingTypeDescription || ""}
                                onChange={(e) => updateContractField("shippingTypeDescription", e.target.value)}
                                placeholder="e.g. Truck-Border"
                              />
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formInco">Incoterm</Label>
                              <Select
                                value={parsed.incoTerm || "CIF"}
                                onValueChange={(val) => updateContractField("incoTerm", val)}
                              >
                                <SelectTrigger id="formInco">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {["CIF", "FOB", "CFR", "EXW", "DDP"].map((inc) => (
                                    <SelectItem key={inc} value={inc}>{inc}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="formShipFrom">Ship From Date</Label>
                              <Input
                                id="formShipFrom"
                                value={parsed.shipFrom || ""}
                                onChange={(e) => updateContractField("shipFrom", e.target.value)}
                                placeholder="YYYYMMDD"
                              />
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formShipTo">Ship To Date</Label>
                              <Input
                                id="formShipTo"
                                value={parsed.shipTo || ""}
                                onChange={(e) => updateContractField("shipTo", e.target.value)}
                                placeholder="YYYYMMDD"
                              />
                            </div>
                            <div>
                              <Label htmlFor="formPOL">Port Of Loading</Label>
                              <Input
                                id="formPOL"
                                value={parsed.portOfLoading || ""}
                                onChange={(e) => updateContractField("portOfLoading", e.target.value)}
                                placeholder="e.g. Croatia"
                              />
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formPOLName">Port Of Loading Name</Label>
                              <Input
                                id="formPOLName"
                                value={parsed.portOfLoadingName || ""}
                                onChange={(e) => updateContractField("portOfLoadingName", e.target.value)}
                                placeholder="e.g. Rijeka Bakar"
                              />
                            </div>
                            <div>
                              <Label htmlFor="formPOD">Port Of Discharge</Label>
                              <Input
                                id="formPOD"
                                value={parsed.portOfDischarge || ""}
                                onChange={(e) => updateContractField("portOfDischarge", e.target.value)}
                                placeholder="e.g. Barbados"
                              />
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formPODName">Port Of Discharge Name</Label>
                              <Input
                                id="formPODName"
                                value={parsed.portOfDischargeName || ""}
                                onChange={(e) => updateContractField("portOfDischargeName", e.target.value)}
                                placeholder="e.g. Bridgetown"
                              />
                            </div>
                            <div>
                              <Label htmlFor="formFinalDest">Final Destination</Label>
                              <Input
                                id="formFinalDest"
                                value={parsed.finalDestination || ""}
                                onChange={(e) => updateContractField("finalDestination", e.target.value)}
                                placeholder="e.g. Pago Pago"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="formFinalDestCountry">Final Destination Country</Label>
                            <Input
                              id="formFinalDestCountry"
                              value={parsed.finalDestinationCountry || ""}
                              onChange={(e) => updateContractField("finalDestinationCountry", e.target.value)}
                              placeholder="e.g. American Samoa"
                            />
                          </div>
                        </div>
                      )}

                      {/* Tab 3: Partners */}
                      {creatorSubTab === "partners" && (
                        <div className="space-y-3 animate-in fade-in duration-150">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formSoldTo">Sold-To BP ID (soldTo)</Label>
                              <Input
                                id="formSoldTo"
                                value={parsed.soldTo || ""}
                                onChange={(e) => updateContractField("soldTo", e.target.value)}
                                placeholder="e.g. 300604"
                              />
                            </div>
                            <div>
                              <Label htmlFor="formSoldToName">Sold-To Name</Label>
                              <Input
                                id="formSoldToName"
                                value={parsed.soldToName || ""}
                                onChange={(e) => updateContractField("soldToName", e.target.value)}
                                placeholder="e.g. FTA Food Solutions PTY LTD"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="formSoldToAddress">Sold-To Party Address</Label>
                            <Input
                              id="formSoldToAddress"
                              value={parsed.soldToPartyAddress || ""}
                              onChange={(e) => updateContractField("soldToPartyAddress", e.target.value)}
                              placeholder="Full Company Address"
                            />
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="formCountry">Country Key</Label>
                              <Input
                                id="formCountry"
                                value={parsed.country || ""}
                                onChange={(e) => updateContractField("country", e.target.value)}
                                placeholder="e.g. AU"
                              />
                            </div>
                            <div>
                              <Label htmlFor="formCountryName">Country Name</Label>
                              <Input
                                id="formCountryName"
                                value={parsed.countryName || ""}
                                onChange={(e) => updateContractField("countryName", e.target.value)}
                                placeholder="e.g. Australia"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Tab 4: Items Editor */}
                      {creatorSubTab === "items" && (
                        <div className="space-y-3 animate-in fade-in duration-150">
                          <div className="flex gap-2 items-center">
                            <div className="flex-1">
                              <Label htmlFor="formItemSelect">Select Item to Edit</Label>
                              <Select
                                value={String(selectedItemIdx)}
                                onValueChange={(val) => setSelectedItemIdx(parseInt(val, 10))}
                              >
                                <SelectTrigger id="formItemSelect">
                                  <SelectValue placeholder="Choose item..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {itemsList.map((item: any, idx: number) => (
                                    <SelectItem key={idx} value={String(idx)}>
                                      Item #{idx + 1} - No: {item.itemNo || "N/A"} - {item.material || "No Material"}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex gap-1.5 self-end">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  const newItem = {
                                    contractQty: "100.000",
                                    contractShip: "0.000",
                                    contractBalance: "100.000",
                                    itemNo: String((itemsList.length + 1) * 10).padStart(6, "0"),
                                    sugarGroup: "Refined Sugar",
                                    sugarPacking: "50KG PP BAG",
                                    material: "S400120002",
                                    matDescription: "Sugar Item Description",
                                    shippingMark: "SHIPPING MARK TEXT",
                                    totalNetWeight: "100000.000",
                                    totalNetWeightUnit: "TON",
                                    totalGrossWeight: "101000.000",
                                    totalGrossWeightUnit: "TON",
                                    grossWeight: "1000.000",
                                    grossWeightUnit: "KG",
                                    orderQty: "0.000",
                                    salesUnit: "TON",
                                    baseUnit: "TON",
                                    matDataText: "Cane sugar specifications"
                                  };
                                  const listKey = parsed.productContracts ? "productContracts" : "soItem";
                                  const updatedList = [...itemsList, newItem];
                                  updateContractField(listKey, updatedList);
                                  setSelectedItemIdx(updatedList.length - 1);
                                }}
                                className="h-9 px-2 text-xs"
                              >
                                + Add
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                disabled={itemsList.length <= 1}
                                onClick={() => {
                                  const listKey = parsed.productContracts ? "productContracts" : "soItem";
                                  const updatedList = itemsList.filter((_: any, idx: number) => idx !== selectedItemIdx);
                                  updateContractField(listKey, updatedList);
                                  setSelectedItemIdx(Math.max(0, selectedItemIdx - 1));
                                }}
                                className="h-9 px-2 text-xs hover:bg-rose-100 hover:text-rose-800 dark:hover:bg-rose-950 dark:hover:text-rose-300"
                              >
                                - Del
                              </Button>
                            </div>
                          </div>

                          {itemsList[selectedItemIdx] && (() => {
                            const item = itemsList[selectedItemIdx];
                            const listName = parsed.productContracts ? "productContracts" : "soItem";
                            const pKey = (field: string) => `${listName}.${selectedItemIdx}.${field}`;

                            return (
                              <div className="space-y-3 border-t border-border/50 pt-3">
                                <div className="grid gap-3 sm:grid-cols-2">
                                  <div>
                                    <Label>Item No (itemNo)</Label>
                                    <Input
                                      value={item.itemNo || ""}
                                      onChange={(e) => updateContractField(pKey("itemNo"), e.target.value)}
                                      placeholder="000010"
                                    />
                                  </div>
                                  <div>
                                    <Label>Material Code</Label>
                                    <Input
                                      value={item.material || ""}
                                      onChange={(e) => updateContractField(pKey("material"), e.target.value)}
                                      placeholder="e.g. S400120001"
                                    />
                                  </div>
                                </div>

                                <div>
                                  <Label>Material Description</Label>
                                  <Input
                                    value={item.matDescription || ""}
                                    onChange={(e) => updateContractField(pKey("matDescription"), e.target.value)}
                                    placeholder="Sugar Description"
                                  />
                                </div>

                                <div className="grid gap-3 sm:grid-cols-2">
                                  <div>
                                    <Label>Sugar Group</Label>
                                    <Input
                                      value={item.sugarGroup || ""}
                                      onChange={(e) => updateContractField(pKey("sugarGroup"), e.target.value)}
                                      placeholder="Sugar Group"
                                    />
                                  </div>
                                  <div>
                                    <Label>Sugar Packing</Label>
                                    <Input
                                      value={item.sugarPacking || ""}
                                      onChange={(e) => updateContractField(pKey("sugarPacking"), e.target.value)}
                                      placeholder="e.g. 50KG PP BAG"
                                    />
                                  </div>
                                </div>

                                <div className="grid gap-3 sm:grid-cols-3">
                                  <div>
                                    <Label>Contract Qty</Label>
                                    <Input
                                      value={item.contractQty || ""}
                                      onChange={(e) => updateContractField(pKey("contractQty"), e.target.value)}
                                      placeholder="e.g. 100.000"
                                    />
                                  </div>
                                  <div>
                                    <Label>Shipped Qty</Label>
                                    <Input
                                      value={item.contractShip || ""}
                                      onChange={(e) => updateContractField(pKey("contractShip"), e.target.value)}
                                      placeholder="e.g. 10.000"
                                    />
                                  </div>
                                  <div>
                                    <Label>Remaining Bal</Label>
                                    <Input
                                      value={item.contractBalance || ""}
                                      disabled
                                      className="bg-muted/40 cursor-not-allowed"
                                    />
                                  </div>
                                </div>

                                <div className="grid gap-3 sm:grid-cols-2">
                                  <div>
                                    <Label>Total Net Weight</Label>
                                    <Input
                                      value={item.totalNetWeight || ""}
                                      onChange={(e) => updateContractField(pKey("totalNetWeight"), e.target.value)}
                                    />
                                  </div>
                                  <div>
                                    <Label>Total Gross Weight</Label>
                                    <Input
                                      value={item.totalGrossWeight || ""}
                                      onChange={(e) => updateContractField(pKey("totalGrossWeight"), e.target.value)}
                                    />
                                  </div>
                                </div>

                                <div>
                                  <Label>Shipping Mark</Label>
                                  <Input
                                    value={item.shippingMark || ""}
                                    onChange={(e) => updateContractField(pKey("shippingMark"), e.target.value)}
                                  />
                                </div>

                                <div>
                                  <Label>Material Data Text</Label>
                                  <textarea
                                    className="w-full text-xs font-mono border rounded px-2 py-1.5 bg-black/20"
                                    rows={2}
                                    value={item.matDataText || ""}
                                    onChange={(e) => updateContractField(pKey("matDataText"), e.target.value)}
                                  />
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      <div className="border border-border/55 rounded-lg p-3 bg-muted/15 text-xs space-y-1">
                        <span className="font-semibold block text-muted-foreground">Active Hook Destination:</span>
                        <div className="font-mono text-[11px] text-primary truncate">
                          POST EMP_CONTRACT_WEBHOOK_URL (defined in backend env config)
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </SectionCard>
                </div>
              )}

              {fullscreenCard !== "api1_form" && (
                <div className={`${fullscreenCard === "api1_json" ? "lg:col-span-12" : "lg:col-span-5"} space-y-6 transition-all duration-300`}>
                  <SectionCard
                    title="JSON Payload Editor"
                    icon={FileJson}
                    onFullscreenToggle={() => setFullscreenCard(fullscreenCard === "api1_json" ? null : "api1_json")}
                    isFullscreen={fullscreenCard === "api1_json"}
                  >
                    <div className="space-y-4">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Modify JSON directly to test NSW edge cases</span>
                        <button
                          onClick={loadDefaultContractPayload}
                          className="text-xs hover:text-primary transition-colors flex items-center gap-1 font-medium text-muted-foreground"
                        >
                          <RefreshCw className="size-3" /> Reset default
                        </button>
                      </div>
                      <textarea
                        rows={16}
                        className="w-full rounded-lg border border-input bg-black/40 px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent leading-relaxed"
                        value={contractPayload}
                        onChange={(e) => setContractPayload(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button onClick={sendContractWebhook} disabled={contractLoading} className="w-full">
                          {contractLoading ? "Sending Webhook..." : "POST Webhook to Client"}
                        </Button>
                      </div>
                      <ResponseBlock resp={contractResp} />
                    </div>
                  </SectionCard>
                </div>
              )}
            </div>
          </div>
        )}

        {/* API No.2: Create SO Tab */}
        {activeTab === "api2" && (
          <div className="space-y-6 animate-in fade-in duration-200">
            <div>
              <h2 className="text-xl font-bold text-foreground">API No.2: Create Sales Order (createSO)</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Inbound REST endpoint called by the platform to register a Sales Order in SAP.
              </p>
            </div>

            <div className="space-y-6">
                <SectionCard title="Received Create SO Requests from Client" icon={Database}>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-border/50 pb-2">
                      <span className="text-xs text-muted-foreground">List of incoming <code>/createSO</code> calls saved to database</span>
                      <div className="flex items-center gap-2">
                        <Input 
                          placeholder="Search Order / EMP No" 
                          value={searchCreateSO}
                          onChange={(e) => setSearchCreateSO(e.target.value)}
                          className="h-7 text-xs w-48"
                        />
                        <Button variant="outline" size="sm" onClick={loadSalesOrders} disabled={salesOrdersLoading} className="h-7 text-xs">
                          <RefreshCw className={`size-3 mr-1.5 ${salesOrdersLoading ? "animate-spin" : ""}`} /> Refresh
                        </Button>
                      </div>
                    </div>

                    {salesOrders.filter((so) => so.action === "create" && ((so.salesOrderNo?.toLowerCase() || "").includes(searchCreateSO.toLowerCase()) || (so.empNo?.toLowerCase() || "").includes(searchCreateSO.toLowerCase()))).length === 0 ? (
                      <div className="py-8 text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg bg-muted/10">
                        No received createSO requests found matching criteria.
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                        {salesOrders
                          .filter((so) => so.action === "create" && ((so.salesOrderNo?.toLowerCase() || "").includes(searchCreateSO.toLowerCase()) || (so.empNo?.toLowerCase() || "").includes(searchCreateSO.toLowerCase())))
                          .map((so) => (
                            <div key={so.id} className="border border-border/60 rounded-lg p-3 bg-muted/20 hover:border-primary/30 transition-all text-xs space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-mono font-bold text-foreground">Order: {so.salesOrderNo || "Error Flag"}</span>
                                <Badge label={so.returnType === "S" ? "SUCCESS" : "ERROR"} ok={so.returnType === "S"} />
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                                <div>EMP Ref: <span className="font-mono text-foreground font-semibold">{so.empNo || "—"}</span></div>
                                <div className="text-right">{formatDate(so.createdAt)}</div>
                              </div>
                              {so.returnMessage && (
                                <div className="text-[10px] text-rose-600 dark:text-rose-400 font-mono bg-rose-50/50 dark:bg-rose-950/20 px-2 py-1 rounded">
                                  Error: {so.returnMessage}
                                </div>
                              )}
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setExpandedSoId(expandedSoId === so.id ? null : so.id)}
                                  className="h-7 flex-1 text-[10px] flex items-center justify-center gap-1"
                                >
                                  <FileJson className="size-3" />
                                  {expandedSoId === so.id ? "Hide JSON" : "Inspect JSON"}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => linkToSoWebhook(so)}
                                  className="h-7 flex-1 text-[10px] flex items-center justify-center gap-1 border-primary/30 text-primary hover:bg-primary/5 font-semibold relative"
                                >
                                  <Send className="size-3" />
                                  Process Webhook (API 3)
                                  {(processAttemptCounts[so.id] || 0) > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[9px] w-4 h-4 flex items-center justify-center rounded-full shadow">
                                      {processAttemptCounts[so.id]}
                                    </span>
                                  )}
                                </Button>
                              </div>
                              {expandedSoId === so.id && (
                                <pre className="p-2.5 bg-black/40 rounded border border-border/50 text-[10px] font-mono whitespace-pre-wrap break-all max-h-[250px] overflow-auto leading-relaxed">
                                  {JSON.stringify(so.requestPayload, null, 2)}
                                </pre>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </SectionCard>
            </div>
          </div>
        )}

        {/* API No.3: Send SO Webhook */}
        {activeTab === "api3" && (
          <div className="space-y-6 animate-in fade-in duration-200">
            <div>
              <h2 className="text-xl font-bold text-foreground">API No.3: Sales Order Update Webhook</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Simulate SAP pushing booking, vessel and cargo shipment details to client.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-12 items-start">
              {/* Left Column: Form Builder (7 cols) */}
              {fullscreenCard !== "api3_json" && (
                <div className={`${fullscreenCard === "api3_form" ? "lg:col-span-12" : "lg:col-span-7"} space-y-6 transition-all duration-300`}>
                  {(() => {
                    let parsedSo: any = {};
                    try {
                      parsedSo = JSON.parse(soPayload);
                    } catch (e) {}

                    const items: any[] = parsedSo.soItem || [];
                    const activeItem = items[soSelectedIdx] || {};

                    return (
                      <SectionCard
                        title="Interactive Payload Creator (Sales Order)"
                        icon={FileJson}
                        onFullscreenToggle={() => setFullscreenCard(fullscreenCard === "api3_form" ? null : "api3_form")}
                        isFullscreen={fullscreenCard === "api3_form"}
                      >
                      <div className="space-y-6">
                        {/* Sub tabs navigation */}
                        <div className="flex border-b border-border/60 pb-px gap-1">
                          {(["header", "logistics", "partners", "items"] as const).map((tab) => (
                            <button
                              key={tab}
                              onClick={() => setSoCreatorSubTab(tab)}
                              className={`px-4 py-2 text-xs font-semibold border-b-2 transition-all capitalize -mb-px ${
                                soCreatorSubTab === tab
                                  ? "border-primary text-primary"
                                  : "border-transparent text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              {tab}
                            </button>
                          ))}
                        </div>

                        {/* SUB TAB: HEADER */}
                        {soCreatorSubTab === "header" && (
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                              <Label>Sales Document No (salesDoc)</Label>
                              <Input
                                value={parsedSo.salesDoc || ""}
                                onChange={(e) => updateSoField("salesDoc", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Booking Number</Label>
                              <Input
                                value={parsedSo.bookingNumber || ""}
                                onChange={(e) => updateSoField("bookingNumber", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Customer PO</Label>
                              <Input
                                value={parsedSo.customerPo || ""}
                                onChange={(e) => updateSoField("customerPo", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Paperless Code</Label>
                              <Input
                                value={parsedSo.paperless || ""}
                                onChange={(e) => updateSoField("paperless", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>EMP Number (empNo)</Label>
                              <Input
                                value={parsedSo.empNo || ""}
                                onChange={(e) => updateSoField("empNo", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Document Status</Label>
                              <select
                                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                                value={parsedSo.docStatus || ""}
                                onChange={(e) => updateSoField("docStatus", e.target.value)}
                              >
                                <option value="Confirmed">Confirmed</option>
                                <option value="Draft">Draft</option>
                                <option value="Processing">Processing</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {/* SUB TAB: LOGISTICS */}
                        {soCreatorSubTab === "logistics" && (
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                              <Label>ETD</Label>
                              <Input
                                value={parsedSo.etd || ""}
                                onChange={(e) => updateSoField("etd", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>ETA</Label>
                              <Input
                                value={parsedSo.eta || ""}
                                onChange={(e) => updateSoField("eta", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Delivery Date</Label>
                              <Input
                                value={parsedSo.deliveryDate || ""}
                                onChange={(e) => updateSoField("deliveryDate", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Port of Loading</Label>
                              <Input
                                value={parsedSo.portOfLoading || ""}
                                onChange={(e) => updateSoField("portOfLoading", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Port of Discharge</Label>
                              <Input
                                value={parsedSo.portOfDischarge || ""}
                                onChange={(e) => updateSoField("portOfDischarge", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Port of Destination</Label>
                              <Input
                                value={parsedSo.portOfDestination || ""}
                                onChange={(e) => updateSoField("portOfDestination", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Final Destination</Label>
                              <Input
                                value={parsedSo.finalDestination || ""}
                                onChange={(e) => updateSoField("finalDestination", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Closing Date</Label>
                              <Input
                                value={parsedSo.closingDate || ""}
                                onChange={(e) => updateSoField("closingDate", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Closing Time</Label>
                              <Input
                                value={parsedSo.closingTime || ""}
                                onChange={(e) => updateSoField("closingTime", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>VGM Deadline</Label>
                              <Input
                                value={parsedSo.vgmDeadline || ""}
                                onChange={(e) => updateSoField("vgmDeadline", e.target.value)}
                              />
                            </div>
                          </div>
                        )}

                        {/* SUB TAB: PARTNERS */}
                        {soCreatorSubTab === "partners" && (
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                              <Label>Sold To ID</Label>
                              <Input
                                value={parsedSo.soldTo || ""}
                                onChange={(e) => updateSoField("soldTo", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Sold To Name</Label>
                              <Input
                                value={parsedSo.soldToName || ""}
                                onChange={(e) => updateSoField("soldToName", e.target.value)}
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <Label>Sold To Address</Label>
                              <Input
                                value={parsedSo.soldToAddress || ""}
                                onChange={(e) => updateSoField("soldToAddress", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Shipper Name</Label>
                              <Input
                                value={parsedSo.shipper || ""}
                                onChange={(e) => updateSoField("shipper", e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Forwarding Agent Name</Label>
                              <Input
                                value={parsedSo.forwardingAgentName || ""}
                                onChange={(e) => updateSoField("forwardingAgentName", e.target.value)}
                              />
                            </div>
                          </div>
                        )}

                        {/* SUB TAB: ITEMS ARRAY EDITOR */}
                        {soCreatorSubTab === "items" && (
                          <div className="space-y-4">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-muted-foreground">Select Active Item:</span>
                              <div className="flex items-center gap-1 flex-1">
                                {items.map((_, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => setSoSelectedIdx(idx)}
                                    className={`px-2.5 py-1 text-xs font-bold rounded border transition-all ${
                                      soSelectedIdx === idx
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-muted/30 border-border text-muted-foreground hover:text-foreground"
                                    }`}
                                  >
                                    #{idx + 1}
                                  </button>
                                ))}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const newItem = {
                                      itemNo: String((items.length + 1) * 10).padStart(6, "0"),
                                      material: "CT1000",
                                      descriptionOfGoods: "Sugar Extra White Grade A",
                                      containertype: "20FT Dry Container",
                                      containerSize: "20GP",
                                      noOfContainer: "1",
                                      orderQtyTon: "25.000",
                                      salesUnitTon: "TON",
                                      totalPackage: "500",
                                      totalPackageUnit: "BAG",
                                      totalGrossWeight: "25100.000",
                                      totalNetWeight: "25000.000",
                                      docCont: [],
                                      docList: []
                                    };
                                    const updated = [...items, newItem];
                                    updateSoField("soItem", updated);
                                    setSoSelectedIdx(updated.length - 1);
                                  }}
                                  className="h-7 text-[10px] ml-auto"
                                >
                                  + Add Item
                                </Button>
                                {items.length > 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const updated = items.filter((_, idx) => idx !== soSelectedIdx);
                                      updateSoField("soItem", updated);
                                      setSoSelectedIdx(Math.max(0, updated.length - 1));
                                    }}
                                    className="h-7 text-[10px] border-rose-500/30 text-rose-600 dark:text-rose-400"
                                  >
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </div>

                            {activeItem && (
                              <div className="grid gap-4 sm:grid-cols-2 border border-border/40 rounded-lg p-3 bg-muted/10">
                                <div>
                                  <Label>Item No</Label>
                                  <Input
                                    value={activeItem.itemNo || ""}
                                    onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.itemNo`, e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>Material</Label>
                                  <Input
                                    value={activeItem.material || ""}
                                    onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.material`, e.target.value)}
                                  />
                                </div>
                                <div className="sm:col-span-2">
                                  <Label>Description of Goods</Label>
                                  <Input
                                    value={activeItem.descriptionOfGoods || ""}
                                    onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.descriptionOfGoods`, e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>Container Type</Label>
                                  <Input
                                    value={activeItem.containertype || ""}
                                    onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.containertype`, e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>No of Container</Label>
                                  <Input
                                    value={activeItem.noOfContainer || ""}
                                    onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.noOfContainer`, e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>Order Qty (Ton)</Label>
                                  <Input
                                    value={activeItem.orderQtyTon || ""}
                                    onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.orderQtyTon`, e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>Gross Weight</Label>
                                  <Input
                                    value={activeItem.totalGrossWeight || ""}
                                    onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.totalGrossWeight`, e.target.value)}
                                  />
                                </div>

                                {/* Container Array (docCont) Editor - Spans full grid width */}
                                <div className="sm:col-span-2 mt-4 border-t border-border/60 pt-4 space-y-4">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-foreground">Containers (docCont)</span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const contArray = activeItem.docCont || [];
                                        const newCont = {
                                          conNo: "TCKU" + Math.floor(1000000 + Math.random() * 9000000),
                                          sealNo: "SL" + Math.floor(100000 + Math.random() * 900000),
                                          tareWeight: "3900",
                                          contPo: activeItem.itemNo || "",
                                          descOfGoods: activeItem.descriptionOfGoods || "",
                                          quantity: "25",
                                          unit: "TON",
                                          netWeight: "25000",
                                          grossWeight: "28900",
                                          cbm: "33.5"
                                        };
                                        const updated = [...contArray, newCont];
                                        updateSoField(`soItem.${soSelectedIdx}.docCont`, updated);
                                        setActiveContIdx(updated.length - 1);
                                      }}
                                      className="h-7 text-[10px]"
                                    >
                                      + Add Container
                                    </Button>
                                  </div>

                                  {(activeItem.docCont || []).length === 0 ? (
                                    <div className="py-4 text-center text-xs text-muted-foreground border border-dashed border-border rounded bg-muted/5">
                                      No containers added. Click "+ Add Container" to append containers to this item.
                                    </div>
                                  ) : (
                                    <div className="space-y-3">
                                      <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                                        {(activeItem.docCont || []).map((_: any, idx: number) => (
                                          <button
                                            key={idx}
                                            type="button"
                                            onClick={() => setActiveContIdx(idx)}
                                            className={`px-2 py-0.5 text-[10px] font-bold rounded border transition-all ${
                                              activeContIdx === idx
                                                ? "bg-primary/90 text-primary-foreground border-primary"
                                                : "bg-muted/30 border-border text-muted-foreground hover:text-foreground"
                                            }`}
                                          >
                                            Cont #{idx + 1}
                                          </button>
                                        ))}
                                        {(activeItem.docCont || []).length > 0 && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              const contArray = activeItem.docCont || [];
                                              const updated = contArray.filter((_: any, idx: number) => idx !== activeContIdx);
                                              updateSoField(`soItem.${soSelectedIdx}.docCont`, updated);
                                              setActiveContIdx(Math.max(0, updated.length - 1));
                                            }}
                                            className="h-5 px-1.5 text-[9px] border-rose-500/20 text-rose-500 hover:bg-rose-500/5 ml-auto"
                                          >
                                            Delete Active
                                          </Button>
                                        )}
                                      </div>

                                      {activeItem.docCont?.[activeContIdx] && (
                                        <div className="grid gap-3 sm:grid-cols-2 border border-border/40 rounded p-3 bg-muted/5 text-xs">
                                          <div>
                                            <Label className="text-[10px]">Container No (conNo)</Label>
                                            <Input
                                              value={activeItem.docCont[activeContIdx].conNo || ""}
                                              onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.docCont.${activeContIdx}.conNo`, e.target.value)}
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-[10px]">Seal No (sealNo)</Label>
                                            <Input
                                              value={activeItem.docCont[activeContIdx].sealNo || ""}
                                              onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.docCont.${activeContIdx}.sealNo`, e.target.value)}
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-[10px]">Tare Weight</Label>
                                            <Input
                                              value={activeItem.docCont[activeContIdx].tareWeight || ""}
                                              onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.docCont.${activeContIdx}.tareWeight`, e.target.value)}
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-[10px]">Quantity</Label>
                                            <Input
                                              value={activeItem.docCont[activeContIdx].quantity || ""}
                                              onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.docCont.${activeContIdx}.quantity`, e.target.value)}
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-[10px]">Gross Weight</Label>
                                            <Input
                                              value={activeItem.docCont[activeContIdx].grossWeight || ""}
                                              onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.docCont.${activeContIdx}.grossWeight`, e.target.value)}
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-[10px]">Net Weight</Label>
                                            <Input
                                              value={activeItem.docCont[activeContIdx].netWeight || ""}
                                              onChange={(e) => updateSoField(`soItem.${soSelectedIdx}.docCont.${activeContIdx}.netWeight`, e.target.value)}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </SectionCard>
                  );
                })()}

                <SectionCard title="Automated Behavior Info" icon={CheckCircle}>
                  <div className="space-y-4 text-xs text-muted-foreground leading-relaxed">
                    <p>
                      <strong>Note:</strong> Calling the <code>/createSO</code> API No.2 mock endpoint automatically triggers this API No.3 Sales Order status webhook to client after a 10 seconds delay.
                    </p>
                    <p>
                      You can use this tab to send a manual status update webhook asynchronously to the configured <code>EMP_SALES_ORDER_WEBHOOK_URL</code>.
                    </p>
                  </div>
                </SectionCard>
              </div>
              )}

              {/* Right Column: Code Editor & Response Block (5 cols) */}
              {fullscreenCard !== "api3_form" && (
                <div className={`${fullscreenCard === "api3_json" ? "lg:col-span-12" : "lg:col-span-5"} space-y-6 transition-all duration-300`}>
                  <SectionCard
                    title="Sales Order Webhook Payload Builder"
                    icon={FileJson}
                    onFullscreenToggle={() => setFullscreenCard(fullscreenCard === "api3_json" ? null : "api3_json")}
                    isFullscreen={fullscreenCard === "api3_json"}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Sends to <code>EMP_SALES_ORDER_WEBHOOK_URL</code></span>
                        <button
                          onClick={loadDefaultSoPayload}
                          className="text-xs hover:text-primary transition-colors flex items-center gap-1 font-medium text-muted-foreground"
                        >
                          <RefreshCw className="size-3" /> Reset default
                        </button>
                      </div>
                      <textarea
                        rows={18}
                        className="w-full rounded-lg border border-input bg-black/40 px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent leading-relaxed"
                        value={soPayload}
                        onChange={(e) => setSoPayload(e.target.value)}
                      />
                      <Button onClick={sendSoWebhook} disabled={soLoading} className="w-full">
                        {soLoading ? "Sending Webhook..." : "Send Sales Order Status Update Webhook"}
                      </Button>
                      <ResponseBlock resp={soResp} />
                    </div>
                  </SectionCard>
                </div>
              )}
            </div>
          </div>
        )}

        {/* API No.4: Update SO */}
        {activeTab === "api4" && (
          <div className="space-y-6 animate-in fade-in duration-200">
            <div>
              <h2 className="text-xl font-bold text-foreground">API No.4: Update Sales Order (updateSO)</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Inbound REST endpoint called by client (EMP) to append logistics, container numbers, and seal tags.
              </p>
            </div>

            <div className="space-y-6">
              <SectionCard title="Received Update SO Requests from Client" icon={Database}>
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-border/50 pb-2">
                    <span className="text-xs text-muted-foreground">List of incoming <code>/updateSO</code> calls saved to database</span>
                    <div className="flex items-center gap-2">
                      <Input 
                        placeholder="Search Order / EMP No" 
                        value={searchUpdateSO}
                        onChange={(e) => setSearchUpdateSO(e.target.value)}
                        className="h-7 text-xs w-48"
                      />
                      <Button variant="outline" size="sm" onClick={loadSalesOrders} disabled={salesOrdersLoading} className="h-7 text-xs">
                        <RefreshCw className={`size-3 mr-1.5 ${salesOrdersLoading ? "animate-spin" : ""}`} /> Refresh
                      </Button>
                    </div>
                  </div>

                  {salesOrders.filter((so) => so.action === "update" && ((so.salesOrderNo?.toLowerCase() || "").includes(searchUpdateSO.toLowerCase()) || (so.empNo?.toLowerCase() || "").includes(searchUpdateSO.toLowerCase()))).length === 0 ? (
                    <div className="py-8 text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg bg-muted/10">
                      No received updateSO requests found matching criteria.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                      {salesOrders
                        .filter((so) => so.action === "update" && ((so.salesOrderNo?.toLowerCase() || "").includes(searchUpdateSO.toLowerCase()) || (so.empNo?.toLowerCase() || "").includes(searchUpdateSO.toLowerCase())))
                        .map((so) => (
                          <div key={so.id} className="border border-border/60 rounded-lg p-3 bg-muted/20 hover:border-primary/30 transition-all text-xs space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-mono font-bold text-foreground">Order: {so.salesOrderNo || "Error Flag"}</span>
                              <Badge label={so.returnType === "S" ? "SUCCESS" : "ERROR"} ok={so.returnType === "S"} />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setExpandedSoId(expandedSoId === so.id ? null : so.id)}
                                className="h-7 flex-1 text-[10px] flex items-center justify-center gap-1"
                              >
                                <FileJson className="size-3" />
                                {expandedSoId === so.id ? "Hide JSON" : "Inspect JSON"}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => linkToSoWebhook(so)}
                                className="h-7 flex-1 text-[10px] flex items-center justify-center gap-1 border-primary/30 text-primary hover:bg-primary/5 font-semibold relative"
                              >
                                <Send className="size-3" />
                                Process Webhook (API 3)
                                {(processAttemptCounts[so.id] || 0) > 0 && (
                                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[9px] w-4 h-4 flex items-center justify-center rounded-full shadow">
                                    {processAttemptCounts[so.id]}
                                  </span>
                                )}
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                              <div>EMP Ref: <span className="font-mono text-foreground font-semibold">{so.empNo || "—"}</span></div>
                              <div className="text-right">{formatDate(so.createdAt)}</div>
                            </div>
                            {so.returnMessage && (
                              <div className="text-[10px] text-rose-600 dark:text-rose-400 font-mono bg-rose-50/50 dark:bg-rose-950/20 px-2 py-1 rounded">
                                Error: {so.returnMessage}
                              </div>
                            )}
                            <div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setExpandedSoId(expandedSoId === so.id ? null : so.id)}
                                className="h-7 w-full text-[10px] flex items-center justify-center gap-1"
                              >
                                <FileJson className="size-3" />
                                {expandedSoId === so.id ? "Hide Received JSON" : "Inspect Received JSON"}
                              </Button>
                            </div>
                            {expandedSoId === so.id && (
                              <pre className="p-2.5 bg-black/40 rounded border border-border/50 text-[10px] font-mono whitespace-pre-wrap break-all max-h-[250px] overflow-auto leading-relaxed">
                                {JSON.stringify(so.requestPayload, null, 2)}
                              </pre>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </SectionCard>
            </div>
          </div>
        )}

        {/* Outbound Logs Tab */}
        {activeTab === "history" && (
          <div className="space-y-6 animate-in fade-in duration-200">
            <div>
              <h2 className="text-xl font-bold text-foreground">SAP Mock History Logs</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Track all CPI inbound operations and outbound webhook status triggers.
              </p>
            </div>

            <Card className="border-white/10 shadow-sm">
              <CardHeader className="bg-muted/40 shrink-0 flex flex-row items-center justify-between border-b border-white/5 py-4">
                <CardTitle className="text-base font-semibold">Sales Orders Mock Store</CardTitle>
                <div className="flex items-center gap-2">
                  <Input 
                    placeholder="Search Order / EMP No" 
                    value={searchHistory}
                    onChange={(e) => setSearchHistory(e.target.value)}
                    className="h-8 text-xs w-48"
                  />
                  <Button variant="outline" size="sm" onClick={loadSalesOrders} disabled={salesOrdersLoading} className="h-8">
                    <RefreshCw className={`size-3.5 mr-1.5 ${salesOrdersLoading ? "animate-spin" : ""}`} /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {salesOrders.filter((so) => !searchHistory || (so.salesOrderNo?.toLowerCase() || "").includes(searchHistory.toLowerCase()) || (so.empNo?.toLowerCase() || "").includes(searchHistory.toLowerCase())).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No calls registered yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-muted/50 border-b border-border text-muted-foreground">
                        <tr>
                          <th className="p-3">ID</th>
                          <th className="p-3">Action</th>
                          <th className="p-3">Sales Order No</th>
                          <th className="p-3">EMP No</th>
                          <th className="p-3">Status</th>
                          <th className="p-3">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {salesOrders.filter((so) => !searchHistory || (so.salesOrderNo?.toLowerCase() || "").includes(searchHistory.toLowerCase()) || (so.empNo?.toLowerCase() || "").includes(searchHistory.toLowerCase())).map((so) => (
                          <tr key={so.id} className="hover:bg-muted/20">
                            <td className="p-3 font-mono">{so.id}</td>
                            <td className="p-3">
                              <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${so.action === "create" ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300"}`}>
                                {so.action}
                              </span>
                            </td>
                            <td className="p-3 font-mono font-semibold">{so.salesOrderNo || "—"}</td>
                            <td className="p-3 font-mono">{so.empNo || "—"}</td>
                            <td className="p-3">
                              <Badge label={so.returnType} ok={so.returnType === "S"} />
                              {so.returnMessage && (
                                <span className="block text-[10px] text-destructive mt-1 font-mono">{so.returnMessage}</span>
                              )}
                            </td>
                            <td className="p-3 text-muted-foreground">{formatDate(so.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-white/10 shadow-sm">
              <CardHeader className="bg-muted/40 shrink-0 flex flex-row items-center justify-between border-b border-white/5 py-4">
                <CardTitle className="text-base font-semibold">Outbound Webhooks Log History</CardTitle>
                <div className="flex items-center gap-2">
                  <Input 
                    placeholder="Search Order / EMP No" 
                    value={searchHistory}
                    onChange={(e) => setSearchHistory(e.target.value)}
                    className="h-8 text-xs w-48"
                  />
                  <Button variant="outline" size="sm" onClick={loadOutboundWebhooks} disabled={outboundLoading} className="h-8">
                    <RefreshCw className={`size-3.5 mr-1.5 ${outboundLoading ? "animate-spin" : ""}`} /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {outboundWebhooks.filter(wh => {
                  if (!searchHistory) return true;
                  const q = searchHistory.toLowerCase();
                  const salesDoc = wh.payload?.salesDoc || wh.payload?.salesNumber || wh.payload?.saleNumber || "";
                  const empNo = wh.payload?.empNo || "";
                  return salesDoc.toLowerCase().includes(q) || empNo.toLowerCase().includes(q);
                }).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No webhooks matching criteria.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-muted/50 border-b border-border text-muted-foreground">
                        <tr>
                          <th className="p-3">ID</th>
                          <th className="p-3">Type</th>
                          <th className="p-3">Destination Endpoint</th>
                          <th className="p-3">HTTP Status</th>
                          <th className="p-3">Result</th>
                          <th className="p-3">Dispatched At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {outboundWebhooks.filter(wh => {
                          if (!searchHistory) return true;
                          const q = searchHistory.toLowerCase();
                          const salesDoc = wh.payload?.salesDoc || wh.payload?.salesNumber || wh.payload?.saleNumber || "";
                          const empNo = wh.payload?.empNo || "";
                          return salesDoc.toLowerCase().includes(q) || empNo.toLowerCase().includes(q);
                        }).map((wh) => (
                          <tr key={wh.id} className="hover:bg-muted/20">
                            <td className="p-3 font-mono">{wh.id}</td>
                            <td className="p-3">
                              <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${wh.webhookType === "CONTRACT" ? "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300" : "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300"}`}>
                                {wh.webhookType}
                              </span>
                            </td>
                            <td className="p-3 font-mono text-muted-foreground max-w-[280px] truncate">{wh.targetUrl}</td>
                            <td className="p-3 font-mono">{wh.statusCode ?? "—"}</td>
                            <td className="p-3">
                              <Badge label={wh.success ? "OK" : "FAIL"} ok={!!wh.success} />
                            </td>
                            <td className="p-3 text-muted-foreground">{formatDate(wh.sentAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        )}
      </div>
    </div>
  );
}

// ─── Initial Payload Constants ──────────────────────────────────────────────

const INITIAL_CREATE_SO = {
  data: {
    soHeader: {
      empNo: "EMP0001234",
      salesOrg: "1000",
      distChnnl: "10",
      division: "00",
      docType: "ZOR",
      refDocNo: "C265007309",
      soldToParty: "0010001234",
      shipToParty: "0010001234",
      notifyParty: "SAMPLE NOTIFY PARTY CO LTD",
      consignee: "SAMPLE CONSIGNEE CO LTD",
      customerPoNo: "CUSTPO123456",
      customerPoDate: "01.01.2025",
      documentDate: "01.01.2025",
      reqDeliveryDate: "30.01.2025",
      shippingType: "10",
      docCurrency: "USD",
      billTo: "0010001234",
      payer: "0010001234",
      salesEmployee: "EMP0001234",
      csEmployee: "EMP0005678",
      templateName: "BILL OF LADING",
      shipDeclDate: "14.01.2025",
      ttPaymentDate: "30.01.2025",
      week: "W03-2025",
      loadingDate: "15.01.2025",
      bookingNo: "BKNG123456",
      vesselName: "EVER GIVEN 123W",
      loadingCountry: "TH",
      loadingPort: "THBKK",
      dischargeCountry: "DE",
      dischargePort: "DEHAM",
      finalDestCountry: "NL",
      finalDestPort: "NLRTM",
      etdDate: "15.01.2025",
      etdDatePod: "15.02.2025",
      emptyPickupLocation: "BANGKOK PORT EMPTY DEPOT",
      fullContReturnLoc: "BANGKOK PORT FULL DEPOT",
      paperlessCode: "Y",
      contClosingDate: "12.01.2025",
      contClosingTime: "17.00.00",
      vgmDate: "13.01.2025",
      vgmTime: "12.00.00",
      fwdAgent: "0010005678",
      millPayer: "1000",
      soItem: [
        {
          itemNo: "000010",
          itemType: "TAN",
          material: "MAT001",
          orderQty: "100",
          salesUnit: "TON",
          plants: "P001",
          storage: "WH01",
          refDocNo: "C265007309",
          refDocItem: "0010",
        },
      ],
      soDocList: [
        {
          issuedBy: "SHIPPER",
          templateId: "TMPL001",
          temName: "BILL OF LADING",
          temCat: "BL",
          temCatDesc: "BILL OF LADING",
          noCopies: "3",
          noOriginal: "3",
        },
      ],
    },
  },
};

const INITIAL_UPDATE_SO = {
  data: {
    soHeader: {
      empNo: "EMP0001234",
      salesOrderNo: "SFO1234567",
      notifyParty: "SAMPLE NOTIFY PARTY CO LTD",
      consignee: "SAMPLE CONSIGNEE CO LTD",
      customerPoNo: "CUSTPO123456",
      bookingNo: "BKNG123456",
      vesselName: "EVER GIVEN 123W",
      loadingCountry: "TH",
      loadingPort: "THBKK",
      dischargeCountry: "DE",
      dischargePort: "DEHAM",
      finalDestCountry: "NL",
      finalDestPort: "NLRTM",
      etdDate: "15.01.2025",
      etdDatePod: "15.02.2025",
      emptyPickupLocation: "BANGKOK PORT EMPTY DEPOT",
      fullContReturnLoc: "BANGKOK PORT FULL DEPOT",
      paperlessCode: "Y",
      contClosingDate: "12.01.2025",
      contClosingTime: "17.00.00",
      vgmDate: "13.01.2025",
      vgmTime: "12.00.00",
      fwdAgent: "0010005678",
      millPayer: "1000",
      borderDate: "17.01.2025",
      fwdContact: "MR. FORWARDING AGENT",
      loadingDate: "15.01.2025",
      certInsNo: "INS-2025-001",
      soItem: [
        {
          itemNo: "000010",
          itemType: "TAN",
          material: "MAT001",
          orderQty: "100",
          salesUnit: "TON",
          plants: "P001",
          noOfContainer: "2",
          noOfTruck: "0",
          descOfGoods: "RAW CANE SUGAR ICUMSA 45",
          soDocCont: [
            {
              containerNo: "TCKU1234567",
              sealNo: "SL123456",
              tareWeight: "3900",
              contPo: "PO-001",
              descOfGoods: "RAW CANE SUGAR ICUMSA 45",
              quantity: "50",
              unit: "TON",
              netWeight: "50000",
              grossWeight: "52500",
              cbm: "67.5",
            },
            {
              containerNo: "MSCU9876543",
              sealNo: "SL654321",
              tareWeight: "3900",
              contPo: "PO-001",
              descOfGoods: "RAW CANE SUGAR ICUMSA 45",
              quantity: "50",
              unit: "TON",
              netWeight: "50000",
              grossWeight: "52500",
              cbm: "67.5",
            },
          ],
        },
      ],
      soDocList: [
        {
          issuedBy: "SHIPPER",
          templateId: "TMPL001",
          temName: "BILL OF LADING",
          temCat: "BL",
          temCatDesc: "BILL OF LADING",
          noCopies: "3",
          noOriginal: "3",
        },
      ],
    },
  },
};
